<template>
  <div class="configuracoes-cobrancas-page">
    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
    >
      <div>
        <h5 class="mb-1">
          Configurações Bancárias
        </h5>

        <small class="text-muted">
          Administração das integrações bancárias vinculadas às contas financeiras.
        </small>
      </div>

      <div class="d-flex flex-wrap gap-2">
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="loading || !podeConfigurar"
          @click="novaConfiguracao"
        >
          <i class="bi bi-plus-lg me-1" aria-hidden="true"></i>
          Nova configuração
        </button>

        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="loading || !podeConfigurar"
          @click="carregarTudo"
        >
          <i class="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
          Atualizar
        </button>
      </div>
    </div>

    <div
      v-if="
        permissoesCarregadas &&
        !podeConfigurar
      "
      class="alert alert-warning"
    >
      Você não possui permissão para administrar configurações bancárias.
    </div>

    <template v-else-if="podeConfigurar">
      <div class="card shadow-sm mb-3">
        <div class="card-body">
          <div class="row g-2 align-items-end">
            <div class="col-md-4">
              <label
                class="form-label"
                for="filtro-adaptador"
              >
                Integração
              </label>

              <select
                id="filtro-adaptador"
                v-model="filters.adaptador"
                class="form-select"
                :disabled="loading"
                @change="aplicarFiltros"
              >
                <option value="">
                  Todas
                </option>

                <option
                  v-for="option in adaptadorOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div class="col-md-3">
              <label
                class="form-label"
                for="filtro-ambiente"
              >
                Ambiente
              </label>

              <select
                id="filtro-ambiente"
                v-model="filters.ambiente"
                class="form-select"
                :disabled="loading"
                @change="aplicarFiltros"
              >
                <option value="">
                  Todos
                </option>

                <option
                  v-for="environment in ambienteOptions"
                  :key="environment"
                  :value="environment"
                >
                  {{ environment }}
                </option>
              </select>
            </div>

            <div class="col-md-3">
              <label
                class="form-label"
                for="filtro-ativo"
              >
                Situação
              </label>

              <select
                id="filtro-ativo"
                v-model="filters.ativo"
                class="form-select"
                :disabled="loading"
                @change="aplicarFiltros"
              >
                <option value="">
                  Todas
                </option>

                <option value="true">
                  Ativas
                </option>

                <option value="false">
                  Inativas
                </option>
              </select>
            </div>

            <div class="col-md-2">
              <button
                type="button"
                class="btn btn-secondary btn-sm w-100"
                :disabled="loading"
                @click="limparFiltros"
              >
                <i class="bi bi-funnel me-1" aria-hidden="true"></i>
                Limpar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="successMessage"
        class="alert alert-success"
      >
        {{ successMessage }}
      </div>

      <div
        v-if="errorMessage"
        class="alert alert-danger"
      >
        {{ errorMessage }}
      </div>

      <div
        v-if="loading"
        class="text-muted py-3"
      >
        Carregando configurações bancárias...
      </div>

      <div
        v-else
        class="card shadow-sm"
      >
        <div class="table-responsive">
          <table class="table table-striped table-hover mb-0 configuracoes-table">
            <thead>
              <tr>
                <th>Conta financeira</th>
                <th>Provedor / integração</th>
                <th>Ambiente</th>
                <th>Situação</th>
                <th>Boleto + Pix</th>
                <th>Credencial</th>
                <th>Certificado</th>
                <th>Webhook</th>
                <th class="text-end">
                  Cobranças
                </th>
                <th>Atualizado em</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="!items.length">
                <td
                  colspan="11"
                  class="text-center text-muted py-4"
                >
                  Nenhuma configuração bancária encontrada.
                </td>
              </tr>

              <tr
                v-for="item in items"
                :key="item.id"
              >
                <td>
                  <strong>
                    {{
                      item.conta_financeira_apelido ||
                      `Conta #${item.conta_financeira_id}`
                    }}
                  </strong>

                  <small
                    v-if="item.conta_financeira_instituicao"
                    class="d-block text-muted"
                  >
                    {{ item.conta_financeira_instituicao }}
                  </small>
                </td>

                <td>
                  <strong>
                    {{ item.provedor }}
                  </strong>

                  <small class="d-block text-muted">
                    {{ item.adaptador }}
                  </small>
                </td>

                <td>
                  {{ item.ambiente }}
                </td>

                <td>
                  <span
                    class="badge"
                    :class="
                      item.ativo
                        ? 'bg-success'
                        : 'bg-secondary'
                    "
                  >
                    {{
                      item.ativo
                        ? 'Ativa'
                        : 'Inativa'
                    }}
                  </span>
                </td>

                <td>
                  <span
                    class="badge"
                    :class="
                      item.permite_boleto_pix
                        ? 'bg-success'
                        : 'bg-secondary'
                    "
                  >
                    {{
                      item.permite_boleto_pix
                        ? 'Habilitado'
                        : 'Não'
                    }}
                  </span>
                </td>

                <td>
                  <span
                    :class="
                      configuredClass(
                        item.credencial_configurada
                      )
                    "
                  >
                    {{
                      configuredLabel(
                        item.credencial_configurada
                      )
                    }}
                  </span>
                </td>

                <td>
                  <span
                    :class="
                      configuredClass(
                        item.certificado_configurado
                      )
                    "
                  >
                    {{
                      configuredLabel(
                        item.certificado_configurado
                      )
                    }}
                  </span>
                </td>

                <td>
                  <span
                    :class="
                      configuredClass(
                        item.webhook_secret_configurado
                      )
                    "
                  >
                    {{
                      configuredLabel(
                        item.webhook_secret_configurado
                      )
                    }}
                  </span>
                </td>

                <td class="text-end">
                  {{ item.quantidade_cobrancas }}
                </td>

                <td class="text-nowrap">
                  {{ formatDateTime(item.atualizado_em) }}
                </td>

                <td>
                  <div class="d-flex flex-wrap gap-1">
                    <button
                      v-if="!item.ativo && estadoEmMutacaoId === null"
                      type="button"
                      class="btn btn-link btn-sm p-1"
                      :disabled="
                        item.ativo ||
                        estadoEmMutacaoId !== null
                      "
                      :title="
                        item.ativo
                          ? 'Desative a configuração antes de editá-la.'
                          : 'Editar configuração'
                      "
                      aria-label="Editar configuração"
                      @click="editarConfiguracao(item)"
                    >
                      <i class="bi bi-pencil-fill text-warning" aria-hidden="true"></i>
                    </button>

                    <button
                      v-if="estadoEmMutacaoId === null"
                      type="button"
                      class="btn btn-link btn-sm p-1"
                      :class="
                        item.ativo
                          ? 'text-danger'
                          : 'text-success'
                      "
                      :disabled="
                        estadoEmMutacaoId !== null
                      "
                      :title="estadoEmMutacaoId === item.id ? 'Processando...' : (item.ativo ? 'Desativar' : 'Ativar')"
                      :aria-label="estadoEmMutacaoId === item.id ? 'Processando...' : (item.ativo ? 'Desativar' : 'Ativar')"
                      @click="alterarEstado(item)"
                    >
                      <i
                        class="bi"
                        :class="item.ativo ? 'bi-x-circle-fill' : 'bi-check-circle-fill'"
                        aria-hidden="true"
                      ></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>

      <div
        v-if="!loading"
        class="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3"
      >
        <div>
          Mostrando {{ startRow }} a {{ endRow }} de
          {{ paginacao.total_itens }} configuração(ões)
        </div>

        <div class="d-flex align-items-center gap-2">
          <BFormSelect
            :model-value="paginacao.limite"
            :options="pageOptions"
            :disabled="loading"
            size="sm"
            class="page-limit"
            aria-label="Quantidade de registros por página"
            @update:model-value="alterarLimitePorPagina"
          />
        </div>

        <div class="d-flex align-items-center">
          <BPagination
            :model-value="paginacao.pagina"
            :per-page="paginacao.limite"
            :total-rows="paginacao.total_itens"
            :disabled="loading"
            size="sm"
            limit="5"
            class="mb-0"
            @update:model-value="irPagina($event - paginacao.pagina)"
          />
        </div>
      </div>
    </template>
  </div>
</template>


<script setup>
import {
  computed,
  onMounted,
  reactive,
  ref
} from 'vue';

import { BFormSelect, BPagination } from 'bootstrap-vue-next';

import {
  useRouter
} from 'vue-router';

import {
  usePermissions
} from '@/composables/usePermissions';

import configuracoesCobrancasApi
  from '@/services/configuracoesCobrancas.api';


const router =
  useRouter();


const {
  fetchPermissions,
  hasPermission
} = usePermissions();


const items =
  ref([]);

const catalogo =
  ref([]);

const loading =
  ref(false);

const errorMessage =
  ref('');

const successMessage =
  ref('');

const estadoEmMutacaoId =
  ref(null);

const permissoesCarregadas =
  ref(false);


const filters =
  reactive({
    adaptador: '',
    ambiente: '',
    ativo: ''
  });


const paginacao =
  reactive({
    pagina: 1,
    limite: 20,
    total_itens: 0,
    total_paginas: 0
  });


const pageOptions = [10, 15, 20, 25, 50].map(value => ({
  value,
  text: `${value} / página`
}));


const startRow = computed(() => paginacao.total_itens === 0
  ? 0
  : (paginacao.pagina - 1) * paginacao.limite + 1);

const endRow = computed(() => Math.min(
  paginacao.pagina * paginacao.limite,
  paginacao.total_itens
));


const podeConfigurar =
  computed(
    () =>
      hasPermission(
        'financeiro_cobrancas_configurar'
      )
  );


const adaptadorOptions =
  computed(
    () => {
      const options =
        new Map();

      for (
        const item
        of catalogo.value
      ) {
        if (
          !options.has(
            item.adaptador
          )
        ) {
          options.set(
            item.adaptador,
            {
              value:
                item.adaptador,

              label:
                `${item.provedor} — ${item.adaptador}`
            }
          );
        }
      }

      return [
        ...options.values()
      ].sort(
        (left, right) =>
          left.label.localeCompare(
            right.label
          )
      );
    }
  );


const ambienteOptions =
  computed(
    () => {
      const values =
        new Set();

      for (
        const item
        of catalogo.value
      ) {
        for (
          const environment
          of item.ambientes
        ) {
          values.add(
            environment
          );
        }
      }

      return [
        ...values
      ].sort();
    }
  );


function apiErrorMessage(
  error,
  fallback =
    'Não foi possível carregar as configurações bancárias.'
) {
  return (
    error?.response?.data?.message ||
    fallback
  );
}


function applyListResult(
  result
) {
  items.value =
    result.data;

  Object.assign(
    paginacao,
    result.paginacao
  );
}


function buildListParams(
  pagina
) {
  return {
    pagina,
    limite:
      paginacao.limite,

    adaptador:
      filters.adaptador ||
      undefined,

    ambiente:
      filters.ambiente ||
      undefined,

    ativo:
      filters.ativo ||
      undefined
  };
}


async function carregarConfiguracoes(
  pagina = paginacao.pagina
) {
  if (!podeConfigurar.value) {
    return;
  }

  loading.value = true;
  errorMessage.value = '';

  try {
    const result =
      await configuracoesCobrancasApi
        .listar(
          buildListParams(
            pagina
          )
        );

    applyListResult(
      result
    );
  } catch (error) {
    errorMessage.value =
      apiErrorMessage(
        error
      );
  } finally {
    loading.value = false;
  }
}


async function carregarTudo() {
  if (!podeConfigurar.value) {
    return;
  }

  loading.value = true;
  errorMessage.value = '';

  try {
    const [
      catalogResult,
      listResult
    ] =
      await Promise.all([
        configuracoesCobrancasApi
          .obterCatalogo(),

        configuracoesCobrancasApi
          .listar(
            buildListParams(
              paginacao.pagina
            )
          )
      ]);

    catalogo.value =
      catalogResult;

    applyListResult(
      listResult
    );
  } catch (error) {
    errorMessage.value =
      apiErrorMessage(
        error
      );
  } finally {
    loading.value = false;
  }
}


async function aplicarFiltros() {
  await carregarConfiguracoes(
    1
  );
}


async function limparFiltros() {
  filters.adaptador = '';
  filters.ambiente = '';
  filters.ativo = '';

  await carregarConfiguracoes(
    1
  );
}


async function alterarLimitePorPagina(value) {
  const limite = Number(value);
  if (loading.value || !pageOptions.some(option => option.value === limite)) {
    return;
  }

  paginacao.limite = limite;
  await carregarConfiguracoes(1);
}


async function irPagina(
  delta
) {
  const target =
    paginacao.pagina +
    delta;

  if (
    target < 1 ||
    (
      paginacao.total_paginas > 0 &&
      target >
        paginacao.total_paginas
    )
  ) {
    return;
  }

  await carregarConfiguracoes(
    target
  );
}


function substituirConfiguracao(
  configuracao
) {
  if (!configuracao) {
    return;
  }

  items.value =
    items.value.map(
      item =>
        Number(item.id) ===
          Number(configuracao.id)
          ? configuracao
          : item
    );
}


async function alterarEstado(item) {
  if (
    !podeConfigurar.value ||
    !item ||
    estadoEmMutacaoId.value !==
      null
  ) {
    return;
  }

  const id =
    Number(item.id);

  if (
    !Number.isSafeInteger(id) ||
    id <= 0
  ) {
    return;
  }

  const ativando =
    item.ativo !== true;

  const confirmou =
    window.confirm(
      ativando
        ? 'Deseja realmente ativar esta configuração bancária?'
        : 'Deseja realmente desativar esta configuração bancária?'
    );

  if (!confirmou) {
    return;
  }

  estadoEmMutacaoId.value =
    id;

  errorMessage.value = '';
  successMessage.value = '';

  try {
    const result =
      ativando
        ? await configuracoesCobrancasApi
            .ativar(id)
        : await configuracoesCobrancasApi
            .desativar(id);

    substituirConfiguracao(
      result.configuracao
    );

    if (result.idempotente) {
      successMessage.value =
        ativando
          ? 'A configuração já estava ativa.'
          : 'A configuração já estava inativa.';
    } else {
      successMessage.value =
        ativando
          ? 'Configuração bancária ativada com sucesso.'
          : 'Configuração bancária desativada com sucesso.';
    }

    try {
      const refreshed =
        await configuracoesCobrancasApi
          .listar(
            buildListParams(
              paginacao.pagina
            )
          );

      applyListResult(
        refreshed
      );
    } catch {
      errorMessage.value =
        'A situação foi alterada, mas não foi possível atualizar a listagem. Use o botão Atualizar.';
    }
  } catch (error) {
    errorMessage.value =
      apiErrorMessage(
        error,
        ativando
          ? 'Não foi possível ativar a configuração bancária.'
          : 'Não foi possível desativar a configuração bancária.'
      );
  } finally {
    estadoEmMutacaoId.value =
      null;
  }
}


function novaConfiguracao() {
  router.push(
    '/financeiro/cobrancas/configuracoes/novo'
  );
}


function editarConfiguracao(item) {
  if (
    !item ||
    item.ativo ||
    !Number.isSafeInteger(
      Number(item.id)
    ) ||
    Number(item.id) <= 0
  ) {
    return;
  }

  router.push(
    `/financeiro/cobrancas/configuracoes/${item.id}`
  );
}


function configuredLabel(
  value
) {
  return value
    ? 'Configurado'
    : 'Não';
}


function configuredClass(
  value
) {
  return [
    'badge',
    value
      ? 'bg-success'
      : 'bg-secondary'
  ];
}


function formatDateTime(
  value
) {
  if (!value) {
    return '—';
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '—';
  }

  return new Intl
    .DateTimeFormat(
      'pt-BR',
      {
        dateStyle: 'short',
        timeStyle: 'short'
      }
    )
    .format(date);
}


onMounted(
  async () => {
    try {
      await fetchPermissions();
    } finally {
      permissoesCarregadas.value =
        true;
    }

    if (
      podeConfigurar.value
    ) {
      await carregarTudo();
    }
  }
);
</script>


<style scoped>
.configuracoes-cobrancas-page {
  width: 100%;
  min-width: 0;
}

.configuracoes-table {
  min-width: 1200px;
}

.configuracoes-table th {
  white-space: nowrap;
  font-size: 0.82rem;
}

.configuracoes-table td {
  vertical-align: middle;
  font-size: 0.86rem;
}

.badge {
  font-weight: 600;
}
.page-limit {
  width: 8rem;
}

:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
</style>
